import { useState, useContext } from "react";
import Modal from "../../components/Modal/Modal";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useToast } from "../../design-system/contexts/ToastContext";
import { AddedEventDisplayModal } from "./../../common/modal";
import {
  getEventDetailsBySponsorCode,
  listEvents,
  createUserSponsorMapping,
} from "./../../common/api";
import { useLiveQuery } from "dexie-react-hooks";
import { events } from "./../../common/db";
import AuthContext from "../../contexts/AuthContext";
import { addAllEventsToIndexDB } from "./../../common/functions";

const EnterSponsorCodeFromHeader = () => {
  const [addSponsorCodeModal, setAddSponsorCodeModal] = useState(true);
  const [sponsorCode, setSponsorCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [addedEventModal, setAddedEventModal] = useState(false);
  const [addedEventDatails, setAddedEventDatails] = useState(false);

  const navigate = useNavigate();
  const Context = useContext(AuthContext);
  const { error: showError } = useToast();

  const allEventItems = useLiveQuery(async () => {
    let indexedDBEvents = await events.toArray();
    return indexedDBEvents.reverse();
  });

  const closeModal = () => {
    setAddSponsorCodeModal(false);
    navigate(-1);
  };

  const mappingEvents = (eventId) => {
    let booleanValue = allEventItems.map((value) => {
      if (value.eventId == eventId) return true;
      else return false;
    });
    if (booleanValue.includes(true)) return true;
    else return false;
  };

  const sendEventDetailsToApi = async (sponsorCode, eventId) => {
    setLoading(true);
    const profileUserId = Context.state.user.attributes["custom:userId"];
    let event_details = {
      userId: profileUserId,
      eventId: eventId,
      sponsorCode: sponsorCode,
    };
    try {
      const res = await createUserSponsorMapping(event_details);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in createUserSponsorMapping:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to add event";
        showError(errorMessage);
        setLoading(false);
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error in createUserSponsorMapping:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to add event";
      showError(errorMessage);
      setLoading(false);
      return false;
    }
  };

  const submitClickHandler = async () => {
    setLoading(true);
    try {
      const res = await getEventDetailsBySponsorCode(sponsorCode);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in getEventDetailsBySponsorCode:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to get event details";
        showError(errorMessage);
        setLoading(false);
        return;
      }

      let eventDetails = JSON.parse(res.data.getEventDetailsBySponsorCode);
      if (eventDetails.statusCode === 200) {
        setAddedEventDatails(eventDetails.body[0]);
        setError("");
        if (mappingEvents(eventDetails.body[0].eventId)) {
          setError("Event is already present");
          setLoading(false);
        } else {
          const success = await sendEventDetailsToApi(
            eventDetails.body[0].sponsorCode,
            eventDetails.body[0].eventId
          );
          if (!success) return;

          setAddSponsorCodeModal(false);
          setAddedEventModal(true);

          // Refresh events list
          try {
            const listRes = await listEvents();

            // Check for GraphQL errors
            if (listRes.errors && listRes.errors.length > 0) {
              console.error("GraphQL errors in listEvents:", listRes.errors);
            } else {
              let allEventDetails = JSON.parse(listRes.data.listEventsForSponsor);
              if (allEventDetails.statusCode == 200) {
                events.clear();
                let allEvents = allEventDetails.body;
                addAllEventsToIndexDB(allEvents);
                if (allEvents.length > 1) {
                  Context.setEventDetails({
                    ...Context,
                    eventDetails: {
                      ...Context.eventDetails,
                      removeHome: false,
                    },
                  });
                }
              }
            }
          } catch (listErr) {
            console.error("Error refreshing events list:", listErr);
          }
          setLoading(false);
        }
      } else {
        setLoading(false);
        if (
          eventDetails.body.includes(
            "sponsorCode does not meet minimum length of 6"
          )
        ) {
          setError("Please enter a valid sponsor code");
        } else {
          setError(eventDetails.body);
        }
      }
    } catch (err) {
      console.error("Error in getEventDetailsBySponsorCode:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to get event details";
      showError(errorMessage);
      setLoading(false);
    }
  };

  const sponsorCodeChangeHandler = (e) => {
    setSponsorCode(e.target.value);
    setError("");
  };

  const onCloseModal = () => {
    setAddedEventModal(false);
    navigate({ pathname: "/events" });
  };

  const accessEventClickHandler = () => {
    navigate({
      pathname: `/events/${addedEventDatails.eventId}/scanDetails`,
      search: `?sponsor=${addedEventDatails.sponsorCode}`,
    });
  };

  return (
    <>
      <Modal
        showModal={addSponsorCodeModal}
        setModal={setAddSponsorCodeModal}
        onCloseModal={closeModal}
        faddedBg={true}
      >
        <div className="modal-input">
          {/* Desktop Close Button */}
          <button
            className="modal-input__close-btn"
            onClick={closeModal}
            aria-label="Close"
          >
            <Icon icon="iconamoon:close" width="20" height="20" />
          </button>

          <p className="modal-input__title">
            Enter unique code to access a new event.
          </p>
          <input
            type="text"
            className={`modal-input__field ${
              error ? "modal-input__field--error" : ""
            }`}
            placeholder="Enter unique code"
            onChange={sponsorCodeChangeHandler}
            value={sponsorCode}
            maxLength={6}
          />
          {error && <p className="modal-input__error">{error}</p>}
          <p className="modal-input__helper">
            Enter the sponsor code shared with you by the organiser via email.
          </p>
          <button
            className="modal-input__btn"
            disabled={!sponsorCode || loading}
            onClick={submitClickHandler}
          >
            {loading && <span className="modal-input__spinner" />}
            Continue
          </button>
        </div>
      </Modal>

      <AddedEventDisplayModal
        addedEventModal={addedEventModal}
        setAddedEventModal={setAddedEventModal}
        onCloseModal={onCloseModal}
        accessEventClickHandler={accessEventClickHandler}
        addedEventDatails={addedEventDatails}
      />
    </>
  );
};

export default EnterSponsorCodeFromHeader;